import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus, FileArrowDown, Trash, Sparkle, Brain, FileText, X,
} from "@phosphor-icons/react";

import { api, fmtDate, BACKEND_URL } from "../lib/api";
import { Surface, Btn, Field, Input, Select, Textarea } from "./Primitives";

const CATEGORIES = [
  "airworthiness_certificate", "registration_certificate", "noise_certificate",
  "radio_license", "elt_registration", "arc", "camo",
  "insurance", "kardex", "poh", "afm",
  "maintenance_manual", "electrical_maintenance_manual",
  "ipc", "service_bulletin", "checklist", "weight_balance",
  "weighing_report", "annual_report", "work_order", "logbook",
  "maintenance_release", "other",
];

/** Per-aircraft Documents tab — uploads, AI-extraction, KARDEX ingest. */
export default function AircraftDocumentsTab({
  aircraftId, isOwner, aircraft, onAircraftUpdated,
}) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("other");
  const [uploadExpiry, setUploadExpiry] = useState("");
  const [busyExtract, setBusyExtract] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [extractFor, setExtractFor] = useState(null);
  const [kardexBusy, setKardexBusy] = useState(false);
  const [kardexResult, setKardexResult] = useState(null);
  const [kardexError, setKardexError] = useState("");
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);
  const kardexFileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/documents", { params: { entity_id: aircraftId } })
      .then((r) => setDocs(r.data || []));
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4500);
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", uploadCategory);
      fd.append("entity_id", aircraftId);
      if (uploadExpiry) fd.append("expiry_date", uploadExpiry);
      await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      flashToast(`Uploaded "${file.name}".`);
      load();
    } catch (err) {
      flashToast(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ── Per-row AI extraction ────────────────────────────────────────────────
  const runExtraction = async (doc) => {
    setBusyExtract(true); setExtractFor(doc); setExtraction(null);
    try {
      // We need to re-upload to /doc-extract because /uploads stores
      // the file but doesn't pipe it through Gemini. The extraction kind
      // is inferred from the document's category.
      const kind = ["insurance", "registration", "arc", "kardex"].includes(doc.category)
        ? doc.category : null;
      if (!kind) {
        flashToast("AI extraction is only available for insurance / registration / ARC / kardex categories.");
        setBusyExtract(false);
        return;
      }
      // Fetch the file (signed URL works from same origin)
      const fileResp = await fetch(`${BACKEND_URL}/api/files/${doc.storage_path}`,
                                    { credentials: "include" });
      const blob = await fileResp.blob();
      const fd = new FormData();
      fd.append("file", new File([blob], doc.original_filename, { type: blob.type }));
      fd.append("kind", kind);
      const r = await api.post(`/aircraft/${aircraftId}/doc-extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtraction(r.data);
      flashToast(`AI extraction complete — review the suggested fields.`);
    } catch (err) {
      flashToast(err?.response?.data?.detail || "Extraction failed.");
    } finally {
      setBusyExtract(false);
    }
  };

  const applyExtraction = async () => {
    if (!extraction?.id) return;
    setBusyExtract(true);
    try {
      const fields = extraction.suggested_patch || {};
      await api.post(`/doc-extractions/${extraction.id}/apply`,
                     { fields });
      flashToast("Suggested fields applied to the aircraft.");
      setExtraction(null);
      setExtractFor(null);
      onAircraftUpdated?.();
    } catch (err) {
      flashToast(err?.response?.data?.detail || "Apply failed.");
    } finally {
      setBusyExtract(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  // ── KARDEX ingest (async background — polls until done) ─────────────────
  const pollKardexJob = async (jobId) => {
    const deadline = Date.now() + 3 * 60 * 1000; // 3 minutes
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const s = await api.get(`/aircraft/${aircraftId}/kardex-ingest/${jobId}`);
        if (s.data?.status === "done") return s.data;
        if (s.data?.status === "failed")
          throw new Error(s.data.error || "Kardex extraction failed");
      } catch (err) {
        // 404 means motorboot is still seeding the job doc — keep polling
        if (err?.response?.status === 404) continue;
        throw err;
      }
    }
    throw new Error("Kardex ingest timed out after 3 minutes");
  };

  const ingestKardex = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setKardexBusy(true); setKardexResult(null); setKardexError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/aircraft/${aircraftId}/kardex-ingest`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      flashToast("KARDEX upload accepted — extracting…");
      const job = await pollKardexJob(jobId);
      setKardexResult({ applied: job.applied, status: job.status });
      flashToast("KARDEX ingest complete — review the summary below.");
      onAircraftUpdated?.();
      load();
    } catch (err) {
      setKardexError(err?.response?.data?.detail || err.message || String(err));
    } finally {
      setKardexBusy(false);
      if (kardexFileRef.current) kardexFileRef.current.value = "";
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="aircraft-documents-tab">
      {/* Upload + Kardex card */}
      <Surface className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">
              Aircraft documents
            </div>
            <div className="text-sm text-zinc-300">
              Upload <strong>any document related to {aircraft?.registration || "this aircraft"}</strong> —
              the AI will categorise it, extract aircraft data into the
              right tabs, and keep the file downloadable here.
            </div>
            <div className="text-[11px] text-zinc-500 font-mono mt-1.5">
              Accepts PDF + images · up to 50 MB per file
            </div>
          </div>
          {isOwner && (
            <div className="flex items-center gap-2">
              <Select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}
                      data-testid="doc-category-select" className="text-xs w-44">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                ))}
              </Select>
              <Input type="date" value={uploadExpiry}
                     onChange={(e) => setUploadExpiry(e.target.value)}
                     data-testid="doc-expiry-input" className="w-40 text-xs" />
              <input ref={fileRef} type="file" hidden onChange={handleUpload}
                     accept=".pdf,image/*" data-testid="doc-upload-input" />
              <Btn disabled={uploading} onClick={() => fileRef.current?.click()}
                   data-testid="doc-upload-btn">
                <Plus size={14} className="inline mr-1.5" />
                {uploading ? "Uploading…" : "Upload"}
              </Btn>
            </div>
          )}
        </div>
      </Surface>

      {/* KARDEX ingest card */}
      {isOwner && (
        <Surface className="p-6 border-amber/30">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber font-mono mb-1 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> KARDEX ingest
              </div>
              <div className="text-sm text-zinc-300 max-w-2xl">
                Drop your Aircraft Status Report (KARDEX) and OwnerCAM will:{" "}
                <strong className="text-amber">extract every tracked component, AD, hours
                counter, ARC/insurance expiry, weighing date</strong>, then auto-create the
                matching aircraft parts and AD compliance rows. Existing rows
                are updated, not duplicated.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input ref={kardexFileRef} type="file" hidden onChange={ingestKardex}
                     accept=".pdf,image/*" data-testid="kardex-ingest-input" />
              <Btn disabled={kardexBusy} onClick={() => kardexFileRef.current?.click()}
                   data-testid="kardex-ingest-btn"
                   className="bg-amber text-black hover:bg-amber-300">
                <Brain size={14} className="inline mr-1.5" />
                {kardexBusy ? "Ingesting…" : "Ingest KARDEX"}
              </Btn>
            </div>
          </div>
          {kardexError && (
            <div className="mt-4 p-3 border border-red-500/40 bg-red-500/5 text-xs text-red-300"
                 data-testid="kardex-error">
              {kardexError}
            </div>
          )}
          {kardexResult && (
            <div className="mt-4 p-3 bg-zinc-950/60 border border-zinc-800 font-mono text-[11px] text-zinc-300"
                 data-testid="kardex-result">
              <div className="text-amber mb-2 text-[10px] uppercase tracking-[0.2em]">Ingest summary</div>
              <div>
                Aircraft fields updated:{" "}
                <span className="text-emerald-300">
                  {kardexResult.applied?.aircraft_fields_updated?.length || 0}
                </span>{" "}
                ({(kardexResult.applied?.aircraft_fields_updated || []).slice(0, 6).join(", ") || "—"}
                {kardexResult.applied?.aircraft_fields_updated?.length > 6 && "…"})
              </div>
              <div>
                Components: <span className="text-emerald-300">+{kardexResult.applied?.components?.created || 0}</span>
                {" · "}
                updated <span className="text-cyan-300">{kardexResult.applied?.components?.updated || 0}</span>
                {" · "}
                skipped <span className="text-zinc-500">{kardexResult.applied?.components?.skipped || 0}</span>
              </div>
              <div>
                ADs: created <span className="text-emerald-300">+{kardexResult.applied?.ads?.compliance_created || 0}</span>
                {" · "}
                library new <span className="text-cyan-300">{kardexResult.applied?.ads?.library_created || 0}</span>
              </div>
              <div>
                Service Bulletins: created <span className="text-emerald-300">+{kardexResult.applied?.sbs?.compliance_created || 0}</span>
                {" · "}
                library new <span className="text-cyan-300">{kardexResult.applied?.sbs?.library_created || 0}</span>
              </div>
            </div>
          )}
        </Surface>
      )}

      {/* Documents list */}
      <Surface className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
            <tr>
              <th className="text-left py-3 px-4">Category</th>
              <th className="text-left py-3 px-4">File</th>
              <th className="text-left py-3 px-4">Uploaded by</th>
              <th className="text-left py-3 px-4">Uploaded</th>
              <th className="text-left py-3 px-4">Expires</th>
              <th className="text-right py-3 px-4 w-44">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="aircraft-documents-table">
            {docs.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-zinc-500">
                  No documents uploaded yet for this aircraft.
                </td>
              </tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-zinc-900 hover:bg-[#1F1F1F]">
                <td className="py-2.5 px-4 capitalize">{(d.category || "—").replace(/_/g, " ")}</td>
                <td className="py-2.5 px-4 font-mono text-xs text-zinc-300 truncate max-w-xs">{d.original_filename}</td>
                <td className="py-2.5 px-4 text-zinc-400">{d.uploaded_by_name}</td>
                <td className="py-2.5 px-4 font-mono text-zinc-400">{fmtDate(d.created_at)}</td>
                <td className="py-2.5 px-4 font-mono text-zinc-400">{d.expiry_date || "—"}</td>
                <td className="py-2.5 px-4 text-right">
                  <a href={`${BACKEND_URL}/api/files/${d.storage_path}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber mr-3"
                     data-testid={`doc-download-${d.id}`}>
                    <FileArrowDown size={14} />
                  </a>
                  {isOwner && ["insurance", "registration", "arc", "kardex"].includes(d.category) && (
                    <button onClick={() => runExtraction(d)}
                            disabled={busyExtract}
                            className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber mr-3 disabled:opacity-40"
                            data-testid={`doc-extract-${d.id}`}
                            title="Run AI extraction">
                      <Brain size={14} />
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={() => remove(d.id)}
                            className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-400"
                            data-testid={`doc-delete-${d.id}`}>
                      <Trash size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Surface>

      {/* AI extraction review modal */}
      {extraction && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
             data-testid="extract-review-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-medium">AI extraction · {extractFor?.original_filename}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  Suggested fields for the aircraft. Click apply to write them.
                </div>
              </div>
              <button onClick={() => { setExtraction(null); setExtractFor(null); }}>
                <X size={20} className="text-zinc-400 hover:text-amber" />
              </button>
            </div>
            <div className="bg-zinc-950/60 border border-zinc-800 font-mono text-[11px] text-zinc-300 p-3 overflow-x-auto">
              <pre className="whitespace-pre-wrap">
{JSON.stringify(extraction.suggested_patch || extraction.fields || {}, null, 2)}
              </pre>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => { setExtraction(null); setExtractFor(null); }}>
                Discard
              </Btn>
              <Btn onClick={applyExtraction} disabled={busyExtract}
                   data-testid="extract-apply-btn">
                {busyExtract ? "Applying…" : "Apply to aircraft"}
              </Btn>
            </div>
          </Surface>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 border border-amber/40 bg-[#1a1209] text-amber text-xs font-mono shadow-xl"
             data-testid="doc-tab-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
